import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { analysisApiRef, AnalysisRefused, AnalysisStatus } from './AnalysisApi';

/** How long to wait after one status response before asking again. */
const POLL_INTERVAL_MS = 3000;

/**
 * How many consecutive status failures to ride out before giving up on a run.
 * Roughly half a minute of retries — long enough for a redeploy or a blip, short enough that a
 * collector which is simply gone stops being polled every three seconds for as long as the tab
 * stays open.
 */
const MAX_CONSECUTIVE_FAILURES = 10;

export type UseAnalysisRun = {
  /** False when `scorecards.analysisUrl` is unset — the caller renders nothing at all. */
  enabled: boolean;
  status?: AnalysisStatus;
  /** Set when the last press was declined; cleared as soon as another press is admitted. */
  refused?: AnalysisRefused;
  /** Set when the request itself failed (not a refusal). */
  error?: Error;
  starting: boolean;
  running: boolean;
  start: () => Promise<void>;
};

/**
 * Drives one System's on-demand analysis (feature 0040): press, then poll until it settles, then
 * tell the caller so the score card can refetch itself.
 *
 * Reads the status once on mount as well, so a reader opening the tab while somebody else's run is
 * in flight — or while this System is inside its cooldown — sees the truth rather than an
 * invitation to press a button that will be refused.
 */
export function useAnalysisRun(
  systemName: string | undefined,
  onCompleted?: () => void,
): UseAnalysisRun {
  const analysisApi = useApi(analysisApiRef);
  const enabled = analysisApi.isEnabled() && Boolean(systemName);

  const [status, setStatus] = useState<AnalysisStatus | undefined>();
  const [refused, setRefused] = useState<AnalysisRefused | undefined>();
  const [error, setError] = useState<Error | undefined>();
  const [starting, setStarting] = useState(false);

  // Bumped by `start` to (re)start the poll loop for a freshly admitted run.
  const [pollToken, setPollToken] = useState(0);

  // Kept in a ref so the polling effect never has to re-subscribe when the callback identity
  // changes — a parent that recreates it each render would otherwise restart the loop forever.
  const onCompletedRef = useRef(onCompleted);
  useEffect(() => {
    onCompletedRef.current = onCompleted;
  }, [onCompleted]);

  // Set when a press is admitted, so a run that finishes between the press and the first poll still
  // notifies the caller. Without it, a very fast run would leave the score card showing stale data.
  const pendingCompletionRef = useRef(false);

  // Also set on admission, to hold the first poll back by one interval. The press has already put
  // the strip into "analyzing" from the 202 itself; polling immediately would overwrite that with
  // whatever the server happens to say a millisecond later, making the UI depend on timing.
  const justAdmittedRef = useRef(false);

  // Incremented whenever the System changes or the hook unmounts. Async work captures the value it
  // started under and refuses to touch state if it has moved on — the poll loop has its own
  // `cancelled` flag, but `start` outlives no effect and needs this.
  const lifecycleRef = useRef(0);

  const running = status?.state === 'running';

  // Everything here is per-System. Clearing on change is what stops a reader who navigates from
  // briefly seeing the System they came from reported as running — or stuck mid-press.
  useEffect(() => {
    lifecycleRef.current += 1;
    setStatus(undefined);
    setRefused(undefined);
    setError(undefined);
    setStarting(false);

    // The refs are per-System too. Left set, the System you navigate TO would inherit the one you
    // left: its first poll held back for no reason, and a spurious "run completed" refetch the
    // moment its status settled.
    justAdmittedRef.current = false;
    pendingCompletionRef.current = false;

    return () => {
      lifecycleRef.current += 1;
    };
  }, [systemName]);

  useEffect(() => {
    if (!enabled || !systemName) {
      return undefined;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    // A just-admitted run IS running, whatever the first poll reports — so the settle transition
    // still notifies the caller.
    const admitted = justAdmittedRef.current;
    justAdmittedRef.current = false;
    let sawRunning = admitted;
    let consecutiveFailures = 0;

    const tick = async () => {
      let next: AnalysisStatus | undefined;

      try {
        next = await analysisApi.getStatus(systemName);
      } catch (caught) {
        if (cancelled) {
          return;
        }

        setError(caught as Error);
        consecutiveFailures += 1;

        // Keep watching a run we know is in flight: a single network blip must not silently end
        // polling and leave the strip stuck on "analyzing" forever. A failure on the opening read,
        // when nothing is running, simply reports itself and stops — and a collector that stays
        // down is given up on rather than polled every three seconds until the tab closes.
        if (sawRunning && consecutiveFailures < MAX_CONSECUTIVE_FAILURES) {
          timer = setTimeout(tick, POLL_INTERVAL_MS);
        }
        return;
      }

      // A response that arrives after this effect was torn down — the reader navigated to another
      // System, or the component unmounted — must change nothing.
      if (cancelled) {
        return;
      }

      // A good answer clears a previous failure; otherwise one blip would leave the error showing
      // for as long as the tab stayed open.
      consecutiveFailures = 0;
      setError(undefined);
      setStatus(next);

      if (next?.state === 'running') {
        sawRunning = true;
        // Scheduled only AFTER the previous response landed, so two status requests are never in
        // flight at once and their answers cannot arrive out of order.
        timer = setTimeout(tick, POLL_INTERVAL_MS);
        return;
      }

      if (sawRunning || pendingCompletionRef.current) {
        pendingCompletionRef.current = false;
        onCompletedRef.current?.();
      }
    };

    if (admitted) {
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    } else {
      void tick();
    }

    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [analysisApi, enabled, systemName, pollToken]);

  const start = useCallback(async () => {
    if (!enabled || !systemName || starting || running) {
      return;
    }

    const operation = lifecycleRef.current;

    setStarting(true);
    setError(undefined);

    try {
      const outcome = await analysisApi.start(systemName);

      // The reader may have navigated away while the press was in flight; this answer is about a
      // System that is no longer on screen.
      if (operation !== lifecycleRef.current) {
        return;
      }

      if (outcome.admitted) {
        setRefused(undefined);
        pendingCompletionRef.current = true;
        justAdmittedRef.current = true;
        // Seed the local state from the admission rather than waiting a poll interval, so the strip
        // switches to "analyzing" the instant the press lands.
        setStatus({
          state: 'running',
          runId: outcome.run.runId,
          completedMetrics: 0,
          totalMetrics: outcome.run.totalMetrics,
          startedAt: outcome.run.startedAt,
        });
      } else {
        setRefused(outcome.refused);
      }

      // Either way the world moved on — a run started, or a cooldown/other holder was reported —
      // so restart the loop to pick up the truth.
      setPollToken(token => token + 1);
    } catch (caught) {
      if (operation === lifecycleRef.current) {
        setError(caught as Error);
      }
    } finally {
      if (operation === lifecycleRef.current) {
        setStarting(false);
      }
    }
  }, [analysisApi, enabled, running, starting, systemName]);

  return { enabled, status, refused, error, starting, running, start };
}
